var FenwickTree      = require("enyo-ui/FenwickTree"),
    RepeaterArranger = require("enyo-ui/RepeaterArranger"),
    kind             = require("enyo/kind"),
    utils            = require("enyo/utils");

require("enyo-ui");

/**
 * [RepeaterListArranger]{@link enyo-ui.RepeaterListArranger} is an arranger for
 * [Repeater]{@link enyo-ui.Repeater} aimed at arranging either a vertical or
 * horizontal list of variable sized items.
 *
 * @ui
 * @class   enyo-ui.RepeaterListArranger
 * @extends enyo-ui.RepeaterArranger
 * @public
 */
module.exports = kind(/** @lends  enyo-ui.RepeaterListArranger.prototype */ {

    /**
     * @private
     */
    name: "enyo-ui.RepeaterListArranger",

    /**
     * @private
     */
    kind: RepeaterArranger,

    /**
     * A reference to the placeholder control instance used to calculate the
     * amount of placeholders.
     *
     * @type    {?enyo.Control}
     * @default null
     * @private
     */
    layoutPlaceholder: null,

    /**
     * The width of a placeholder in the list.
     *
     * @type    {number}
     * @default 0
     * @private
     */
    placeholderWidth: 0,

    /**
     * The height of a placeholder in the list.
     *
     * @type    {number}
     * @default 0
     * @private
     */
    placeholderHeight: 0,

    /**
     * @private
     */
    options: /** @lends  enyo-ui.RepeaterListArranger.prototype */ {

        /**
         * The direction of the arrangement. Supported options are:
         *  - vertical
         *  - horizontal
         *
         * @type    {string}
         * @default "vertical"
         * @public
         */
        direction: "vertical",

        /**
         * A default size for repeater child controls.
         *
         * @type    {number}
         * @default 100
         * @public
         */
        defaultChildSize: 100,

        /**
         * The maximum amount of children in the repeater. This value is required
         * for the [childSizeManager]{@link
         * enyo-ui.RepeaterListArranger#childSizeManager} to work.
         *
         * @type    {number}
         * @default 65535
         * @public
         */
        maxLength: 65535
    },

    /**
     * A [Fenwick tree]{@link enyo-ui.FenwickTree} used to manage the size and
     * position of variable-size items.
     *
     * @type {enyo-ui.FenwickTree}
     * @private
     */
    childSizeManager: null,

    /**
     * @method
     * @private
     */
    constructor: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);
            this.repeater.observe("length", utils.bindSafely.apply(null, [this, this.lengthChanged]));
            this.createChildSizeManager();
            this.createLayoutPlaceholder();
        };
    }),

    /**
     * Instantiate a FenwickTree to manage the sizes of list children.
     *
     * @private
     */
    createChildSizeManager: function() {
        this.childSizeManager = new FenwickTree({
            defaultValue: this.getOption("defaultChildSize"),
            maxLength:    this.getOption("maxLength")
        });
    },

    /**
     * Creates a placeholder control which will only be used to calculate the
     * dimensions of placeholders.
     *
     * We don't let the placeholder occupy any space in the DOM except when
     * calculating it's dimensions.
     *
     * @private
     */
    createLayoutPlaceholder: function() {
        var repeater       = this.repeater,
            childContainer = repeater.getChildContainer(),
            layoutPlaceholderKind,
            layoutPlaceholderOptions;

        // Create a placeholder component if possible
        if (repeater.getPlaceholderKind && repeater.getPlaceholderOptions) {
            layoutPlaceholderKind    = repeater.getPlaceholderKind();
            layoutPlaceholderOptions = repeater.getPlaceholderOptions(layoutPlaceholderKind);
        }

        if (layoutPlaceholderKind && layoutPlaceholderOptions) {
            this.layoutPlaceholder   = childContainer.createComponent({
                kind:        layoutPlaceholderKind,
                canGenerate: false,
                owner:       childContainer,
                parent:      childContainer,
                repeater:    repeater
            }, layoutPlaceholderOptions);
            this.layoutPlaceholder.addClass("enyo-ui-repeater__layout-placeholder");
        }
        else {
            this.layoutPlaceholder = null;
        }
    },

    /**
     * Convenience method to replace the current layoutPlaceholder. Call this
     * method if for some reason you need another type of layoutPlaceholder for
     * your reflows.
     *
     * @public
     */
    resetLayoutPlaceholder: function() {
        this.layoutPlaceholder && this.layoutPlaceholder.destroy();
        this.createLayoutPlaceholder();
    },

    /**
     * Length changed, update childSizeManager's maxLength if needed.
     *
     * @private
     */
    lengthChanged: function() {
        var length    = this.repeater.length,
            maxLength = this.getOption("maxLength");

        if (length > maxLength) {
            this.childSizeManager.maxLength = length;
            this.childSizeManager.msb       = FenwickTree.mostSignificantBit(length);
        }
    },

    /**
     * Calculates the properties of the list arrangement.
     *
     * @public
     */
    reflow: function() {
        var childContainer = this.repeater.getChildContainer(),
            direction      = this.getOption("direction");

        // Container required
        if (!childContainer.hasNode()) {
            return;
        }

        // If we're using placeholders, use placeholderDimensions as
        // defaultChildSize.
        if (this.layoutPlaceholder) {

            // Calculate the dimensions of the placeholder
            this.calcPlaceholderDimensions();

            // Reset the childSizeManager to use the placeholderDimensions
            this.childSizeManager.reset(direction === "vertical"
                ? this.placeholderHeight
                : this.placeholderWidth);
        }

        // No placeholders, just use defaultChildSize option.
        else {
            this.childSizeManager.reset(this.getOption("defaultChildSize"));
        }
    },

    /**
     * Calculates the placeholder dimensions.
     *
     * @private
     */
    calcPlaceholderDimensions: function() {
        var placeholder = this.layoutPlaceholder,
            placeholderNode;

        // Render placeholder as needed
        if (placeholder.owner.hasNode() && !placeholder.hasNode()) {
            placeholder.canGenerate = true;
            placeholder.addStyles({
                position:   "absolute",
                visibility: "hidden",
                display:    "none"
            });
            placeholder.render();
        }

        // Get node
        placeholderNode = placeholder.hasNode();

        // No placeholder, no dimensions
        if (!placeholderNode) {
            this.placeholderWidth  = 0;
            this.placeholderHeight = 0;
        }

        // Get placeholder dimensions
        else {
            placeholder.applyStyle("display", "block");
            this.placeholderWidth  = placeholderNode.clientWidth;
            this.placeholderHeight = placeholderNode.clientHeight;
            placeholder.applyStyle("display", "none");
        }
    },

    /**
     * Gets the amount of placeholders needed to fill a viewport of the provided
     * dimensions.
     *
     * @param  {number}  width   The width
     * @param  {number}  height  The height
     * @return {number}  The amount of placeholders.
     * @public
     */
    getPlaceholderCount: function(width, height) {
        var direction         = this.getOption("direction"),
            placeholderWidth  = this.placeholderWidth,
            placeholderHeight = this.placeholderHeight;

        // Enough to fill the viewport height, plus one extra in case of
        // scrolling.
        if (direction === "vertical" && placeholderHeight > 0) {
            return Math.min(
                this.repeater.length,
                Math.ceil(height / placeholderHeight) + 1
            );
        }

        // Enough to fill the viewport width, plus one extra in case of
        // scrolling.
        if (direction === "horizontal" && placeholderWidth > 0) {
            return Math.min(
                this.repeater.length,
                Math.ceil(width / placeholderWidth) + 1
            );
        }

        // No placeholder dimensions: no placeholders.
        return 0;
    },

    /**
     * Get the size of the entire arrangement. Should have at least a width or
     * height property.
     *
     * @return {Object}  The arrangement size.
     * @public
     */
    getArrangementSize: function() {
        var length = this.repeater.length,
            size   = {},
            sizeProp;

        sizeProp       = this.getOption("direction") === "vertical" ? "height" : "width";
        size[sizeProp] = length ? this.childSizeManager.getSum(length) : 0;

        return size;
    },

    /**
     * Gets a sorted set of indexes of children within the arrangement that
     * intersect with the provided bounds.
     *
     * The incoming bounds should be defined in pixels, relative to the top-left
     * corner of the childContainer.
     *
     * @param  {Object}  bounds         The bounds
     * @param  {number}  bounds.top     The top edge of the bounding box in
     *                                  pixels from the top of the grid
     * @param  {number}  bounds.left    The left edge of the bounding box in
     *                                  pixels from the left of the grid
     * @param  {number}  bounds.right   The right edge of the bounding box in
     *                                  pixels from the left of the grid
     * @param  {number}  bounds.bottom  The bottom edge of the bounding box in
     *                                  pixels from the top of the grid
     * @return {Array}   Array of indexes
     * @public
     */
    getArrangementIndexes: function(bounds) {
        var length  = this.repeater.length,
            lowerBound,
            upperBound,
            minIndexLow,
            minIndexHigh,
            minIndex,
            maxIndex,
            indexes = [],
            index;

        // No data
        if (!length) {
            return indexes;
        }

        // Get vertical or horizontal bounds
        if (this.getOption("direction") === "vertical") {
            // Out of bounds
            if (bounds.top > this.childSizeManager.getSum(length)
                || bounds.bottom <= 0) {
                return indexes;
            }

            lowerBound = bounds.top || 0;
            upperBound = bounds.bottom;
        }
        else {
            if (bounds.left > this.childSizeManager.getSum(length)
                || bounds.right <= 0) {
                return indexes;
            }

            lowerBound = bounds.left || 0;
            upperBound = bounds.right;
        }

        // Highest index with sum < to upperBound.
        maxIndex = Math.min(length - 1, this.childSizeManager.lowerBound(upperBound));

        // Highest index with sum <= to lowerBound.
        minIndexHigh = Math.min(maxIndex, this.childSizeManager.upperBound(lowerBound));

        // The index immediately after the highest index with sum < lowerBound
        if (lowerBound > 0) {
            minIndexLow  = Math.min(maxIndex, this.childSizeManager.lowerBound(lowerBound)) + 1;
        }
        else {
            // There is no index with sum < 0
            minIndexLow = 0;
        }

        // If minIndexLow < minIndexHigh, there are 0-height items before
        // minIndexHigh. We want to include those.
        minIndex = minIndexLow < minIndexHigh ? minIndexLow : minIndexHigh;

        // Build index range
        for (index = minIndex; index <= maxIndex; index++) {
            indexes.push(index);
        }

        return indexes;
    },

    /**
     * Returns the distance (in pixels) between the top edge of the child
     * control at the given index, and the top edge of the childContainer.
     *
     * @param  {number}  index  The index
     * @return {number}  The offset
     * @public
     */
    getOffsetTop: function(index) {
        var direction = this.getOption("direction");

        if (direction === "vertical" && index > 0) {
            return this.childSizeManager.getSum(index);
        }

        return 0;
    },

    /**
     * Returns the distance (in pixels) between the left edge of the child
     * control at the given index, and the left edge of the childContainer.
     *
     * @param  {number}  index  The index
     * @return {number}  The offset
     * @public
     */
    getOffsetLeft: function(index) {
        var direction = this.getOption("direction");

        if (direction === "horizontal" && index > 0) {
            return this.childSizeManager.getSum(index);
        }

        return 0;
    },

    /**
     * Gets the index of the child positioned closest to the provided
     * coordinates.
     *
     * The incoming coordinates should be defined in pixels, relative to the
     * top-left corner of the childContainer.
     *
     * @param  {number}   x  The coordinates on the `x` axis.
     * @param  {number}   y  The coordinates on the `y` axis.
     * @return {?number}  The index, or `null` if repeater has 0 length
     * @public
     */
    getNearestIndex: function(x, y) {
        var direction = this.getOption("direction"),
            length    = this.repeater.length;

        return length
            ? Math.min(length, this.childSizeManager.upperBound(direction === "vertical" ? y : x))
            : null;
    },

    /**
     * Returns the scroll offset (in pixels) that would align the top edge of
     * the child arranged at the provided index with the top edge of a scrolled
     * view of the childContainer.
     *
     * @param  {number}  index  The index
     * @return {number}  The offset
     * @public
     */
    getScrollTop: function(index) {
        return this.getOffsetTop(index);
    },

    /**
     * Returns the scroll offset (in pixels) that would align the left edge of
     * the child arranged at the provided index with the left edge of a scrolled
     * view of the childContainer.
     *
     * @param  {number}  index  The index
     * @return {number}  The offset
     * @public
     */
    getScrollLeft: function(index) {
        return this.getOffsetLeft(index);
    },

    /**
     * Saves size of the child associated with the provided index. Returns
     * whether the size changed.
     *
     * @param  {number}   index  The index
     * @param  {number}   size   The size
     * @return {boolean}  `true` if size changed, `false` otherwise
     * @protected
     */
    setSizeAtIndex: function(index, size) {
        var oldSize = this.getSizeAtIndex(index);

        this.childSizeManager.setValue(index + 1, size);

        return oldSize !== size;
    },

    /**
     * Unsets any size stored for the provided index, effectively returning it
     * to the default size. Returns whether the size changed.
     *
     * @param  {number}   index  The index
     * @return {boolean}  `true` if size changed, `false` otherwise
     * @protected
     */
    unsetSizeAtIndex: function(index) {
        var oldSize = this.getSizeAtIndex(index);

        this.childSizeManager.unsetValue(index + 1);

        return oldSize !== this.childSizeManager.getDefaultValue();
    },

    /**
     * Gets the size stored for the provided index, if any.
     *
     * @param  {number}  index  The index
     * @return {number}  The size
     * @protected
     */
    getSizeAtIndex: function(index) {
        var size = this.childSizeManager.getValue(index + 1);

        if (size === null) {
            size = this.childSizeManager.getDefaultValue();
        }

        return size;
    },

    /**
     * Renders the child if needed. Gets the child's size and stores it on the
     * child itself.
     *
     * @param {enyo.Control}  child  The child
     * @public
     */
    drawChild: function(child) {
        var childContainer   = this.repeater.getChildContainer(),
            rendered         = child.hasNode(),
            showing          = child.showing,
            rect,
            direction        = this.getOption("direction"),
            defaultChildSize = this.getOption("defaultChildSize"),
            childBounds      = {
                width:  direction === "horizontal" ? defaultChildSize : 0,
                height: direction === "vertical" ? defaultChildSize : 0
            };

        // Render child as needed.
        if (childContainer.hasNode() && !rendered) {
            child.canGenerate = true;
            child.addStyles({
                position: "absolute",
                top:      0,
                left:     0
            });
            child.render();
        }

        // Proceed if child has a node now
        if (child.hasNode()) {
            // Determine the child's bounds
            if (!showing) {
                child.applyStyle("visibility", "hidden");
                child.show();
            }
            rect        = child.hasNode().getBoundingClientRect();
            childBounds = {
                width:  rect.right - rect.left,
                height: rect.bottom - rect.top
            };

            if (!showing) {
                child.applyStyle("visibility", null);
                child.hide();
            }
        }

        // For use internal to the arranger.
        if (direction === "vertical") {
            child.set("repeaterSize", childBounds.height);
        }
        else {
            child.set("repeaterSize", childBounds.width);
        }

        // For use outside of the arranger.
        child.set("repeaterBounds", childBounds);
    }

});
